import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography, LinearProgress, Alert } from "@mui/material";
import API_ENDPOINTS from "../../../apis/endpoints";
import useAuth from "../../../context";
import VoiceCloningProcess from "./VoiceCloningProcess";

function VoiceCloningUploadProcess({ soundName, folderId, emotions }) {
    const auth = useAuth();
    const [uploadedCount, setUploadedCount] = useState(0);
    const [totalFiles, setTotalFiles] = useState(0);
    const [uploading, setUploading] = useState(true);
    const [showCloningProcess, setShowCloningProcess] = useState(false);
    const storedDeviceId = localStorage.getItem("device-id");
    const [uploadedFileIds, setUploadedFileIds] = useState({});

    useEffect(() => {
        setTotalFiles(
            emotions?.reduce(
                (total, emotion) => total + emotion.files.length,
                0
            )
        );
    }, [emotions]);

    const uploadFiles = useCallback(async () => {
        const uploadedFileIds = {};

        for (const emotion of emotions) {
            uploadedFileIds[emotion.name] = [];

            for (const file of emotion.files) {
                try {
                    const formData = new FormData();
                    formData.append("folder_id", folderId);
                    formData.append("folder_type", "voice");
                    formData.append("file_name", file.name);
                    formData.append("file_type", file.type);
                    formData.append("file_size", file.size.toString());

                    const uploadResponse = await fetch(
                        `${API_ENDPOINTS.STORAGE}`,
                        {
                            method: "POST",
                            headers: {
                                Authorization: `Bearer ${auth.accessToken}`,
                                "x-cc-device-id": storedDeviceId,
                            },
                            body: formData,
                        }
                    );

                    if (!uploadResponse.ok) {
                        throw new Error(
                            `Failed to initiate upload for file: ${file.name}`
                        );
                    }

                    const uploadData = await uploadResponse.json();
                    const { file_id, upload_url } = uploadData.data;

                    const putResponse = await fetch(upload_url, {
                        method: "PUT",
                        body: file,
                        headers: {
                            "Content-Type": file.type,
                        },
                    });

                    if (!putResponse.ok) {
                        throw new Error(`Failed to upload file: ${file.name}`);
                    }

                    uploadedFileIds[emotion.name].push(file_id);
                    setUploadedCount((prev) => prev + 1);
                } catch (error) {
                    console.error("Error uploading file:", error);
                }
            }
        }

        setUploading(false);
        return uploadedFileIds;
    }, [auth.accessToken, emotions, folderId, storedDeviceId]);

    useEffect(() => {
        if (totalFiles > 0) {
            uploadFiles().then(setUploadedFileIds);
        }
    }, [totalFiles, uploadFiles]);

    const handleContinue = async () => {
        try {
            for (const emotion of emotions) {
                const fileIds = uploadedFileIds[emotion.name];
                if (fileIds.length === 0) {
                    console.warn(
                        `No valid file IDs for emotion: ${emotion.name}`
                    );
                    continue;
                }

                const formData = new FormData();
                formData.append("attachments", fileIds.join(","));
                formData.append("emotion", emotion.name);

                const response = await fetch(
                    `${API_ENDPOINTS.AI_MODEL_VOICE}${folderId}/`,
                    {
                        method: "PUT",
                        headers: {
                            Authorization: `Bearer ${auth.accessToken}`,
                            "x-cc-device-id": storedDeviceId,
                        },
                        body: formData,
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(
                        `Failed to start modeling for ${emotion.name}: ${errorData.message}`
                    );
                }
            }
            setShowCloningProcess(true);
        } catch (error) {
            console.error("Error starting modeling:", error);
            // You might want to show an error message to the user here
        }
    };

    const progress = (uploadedCount / totalFiles) * 100;

    if (showCloningProcess) {
        return (
            <VoiceCloningProcess onBack={() => setShowCloningProcess(false)} />
        );
    }

    return (
        <div style={{ padding: "20px" }}>
            <Typography variant="h5">
                Uploading Sounds ({uploadedCount}/{totalFiles})
            </Typography>
            <Typography variant="body1">
                Now sit back and relax. Our system is uploading sounds & cloning
                will start once uploading is done. Don't worry we will notify
                you once cloning is complete.
            </Typography>
            <Box sx={{ width: "100%", mt: 2 }}>
                <LinearProgress variant="determinate" value={progress} />
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
                {uploadedCount} out of {totalFiles} files uploaded
            </Typography>
            {totalFiles === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Please ensure you have uploaded at least one audio file
                    before proceeding.
                </Alert>
            )}
            <Button
                variant="contained"
                sx={{ mt: 3 }}
                onClick={handleContinue}
                disabled={uploading || totalFiles === 0}
            >
                {uploading ? "Uploading..." : "Continue"}
            </Button>
        </div>
    );
}
VoiceCloningUploadProcess.propTypes = {
    soundName: PropTypes.string.isRequired,
    folderId: PropTypes.string.isRequired,
    emotions: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            files: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string.isRequired,
                    type: PropTypes.string.isRequired,
                    size: PropTypes.number.isRequired,
                })
            ).isRequired,
        })
    ).isRequired,
};

export default VoiceCloningUploadProcess;
